/**
 * The card, drawn from the template document: any view of the opening
 * sequence, or the printed sheet. Every zone shows its number and can be
 * selected; a zone with a picture shows it exactly as the paper will.
 *
 * A picture is placed ONCE, on the printed page (`placeImage`: the default
 * fit plus the user's own move / zoom / turn), and each view shows it through
 * that piece of paper's own page-to-view transform - so what a view shows is
 * what folding the print really shows. Notes are laid out once, in the zone's
 * upright frame (`layoutText`), and carried the same way.
 *
 * Zone numbers never sit on top of the picture being worked on: small zones
 * and the selected zone get their number outside the card, on a thin leader
 * line. `mode: 'clean'` draws the card with no numbers, outlines or tints -
 * what the preview and the print use.
 */
import { useEffect, useMemo, useRef, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import {
  DEFAULT_TEXT, DEFAULT_TRANSFORM, centroid, compose, frameOutlines, invert, layoutText, limitTransform, partShift, placeImage, toSvg, zoneFrame,
  type Affine, type ImageTransform, type PaperStyle, type PrintGuides, type Pt, type TemplateDocument, type TemplateView, type TemplateZone,
  type CodeSpot, type MarkSpot, type TextLayout, type ZoneSlot, type ZoneSymbol, type ZoneText,
} from '@foldmycard/scene';
import type { ImageItem } from './api.ts';
import { SymbolMark } from './symbol.tsx';
import { FoldLines, FoldMarkers } from './fold-guides.tsx';
import { BrandAt, BrandMark, brandOnSheet, isBrandView } from './brand.tsx';
import { hasPaper, PaperDefs, PaperFill } from './paper.tsx';
import { measure, useFontsVersion } from './measure.ts';
import { qrModules, qrPath } from './qr.ts';

export interface ShownImage { href: string; width: number; height: number; transform?: ImageTransform }

/**
 * What a zone shows of a picture: the photograph, or its cartoon look when the
 * zone asks for it and one has been made. Both are the same size, so the move /
 * zoom / turn carries over unchanged.
 */
export function shownImage(img: ImageItem, slot: ZoneSlot | undefined, transform = slot?.transform): ShownImage {
  const look = slot?.look === 'cartoon' && img.cartoon ? img.cartoon : slot?.look === 'bw' && img.bw ? img.bw : img;
  return { href: look.urls.preview, ...look.sizes.preview, transform };
}

export interface CardProps {
  template: TemplateDocument;
  /** zone number -> the picture in it */
  pictures: Map<number, ShownImage>;
  /** zone number -> its note */
  notes?: Map<number, ZoneText>;
  /** zone number -> the symbol in its symbol frame (symbol.tsx, 2026-10-31) */
  symbols?: Map<number, ZoneSymbol>;
  /** The paper behind it all (paper.tsx); absent is white. */
  paper?: PaperStyle;
  selected: number | null;
  onSelect?: (zone: number) => void;
  /** The user moved or zoomed the selected zone's picture; `done` at the end of a gesture. */
  onAdjust?: (zone: number, transform: ImageTransform, done: boolean) => void;
  /** 'fill' keeps the zone covered while dragging; 'free' lets the picture go anywhere. */
  placement?: 'fill' | 'free';
  mode?: 'edit' | 'clean';
  showNumbers?: boolean;
  /** Fill the view with the selected zone (the enlarged view). */
  focus?: boolean;
  /** The whole card in black and white (ProjectDocument.mono): pictures, paper and writing in greys. */
  mono?: boolean;
  /**
   * An invitation (2026-10-21, invite.tsx): the message frame `zone` keeps a square for the RSVP
   * code of `url` - one guest's page - with `label` under it ("Scan to RSVP").
   */
  invite?: InviteCode;
  /** The zones left plain (ZoneSlot.plain): the paper covers them, as everywhere around the shapes. */
  plain?: Set<number>;
  /** A double click on a zone: the editor zooms in on it (2026-10-17). */
  onZoom?: (zone: number) => void;
  /**
   * Dragging the selected zone moves its SHAPE up or down instead of its picture (zones 10
   * and 11 in Reveal 1, 2026-10-17): how far down the drag has gone, in the view's mm;
   * `done` when it ends.
   */
  onSlide?: (zone: number, downMm: number, done: boolean) => void;
}

const pts = (p: Pt[]) => p.map((q) => `${q[0]},${q[1]}`).join(' ');
const area = (p: Pt[]) => Math.abs(p.reduce((s, a, i) => {
  const b = p[(i + 1) % p.length];
  return s + a[0] * b[1] - a[1] * b[0];
}, 0) / 2);
const IDENTITY: Affine = [1, 0, 0, 1, 0, 0];
/** The note an invitation's frame has while its message is still empty: the code goes in all the same. */
const EMPTY_NOTE: ZoneText = { content: '', shape: 'zone', language: 'en' };
const FONT_FAMILIES = ["'Noto Sans'", "'Noto Serif'", "'Caveat'", "'Noto Naskh Arabic'", "'Noto Sans Hebrew'"];

/** Page placement of each zone's picture. */
export function usePlacements(template: TemplateDocument, pictures: Map<number, ShownImage>) {
  return useMemo(() => {
    const out = new Map<number, Affine>();
    for (const z of template.zones) {
      const img = pictures.get(z.number);
      // placed on the frame as a whole - for a frame printed in pieces, the whole they make when folded
      if (img) out.set(z.number, placeImage(frameOutlines(z), z.up, img, img.transform));
    }
    return out;
  }, [template, pictures]);
}

/** An invitation's code on the card: the frame it goes in, the link, and the words under it. */
export interface InviteCode { zone: number; url: string; label: string }
/** The RSVP code's side and its label's size, mm: big enough for any phone camera at arm's length. */
export const CODE_REQUEST = { sizeMm: 22, labelMm: 2.6 };

/** Each zone's note, laid out in its upright frame - redone when the fonts arrive. */
export function useLayouts(template: TemplateDocument, notes: Map<number, ZoneText> | undefined, invite?: InviteCode) {
  const fonts = useFontsVersion(FONT_FAMILIES);
  return useMemo(() => {
    const out = new Map<number, TextLayout>();
    for (const [n, note] of notes || []) {
      const z = template.zones.find((x) => x.number === n);
      const code = invite?.zone === n ? { code: CODE_REQUEST } : {};
      if (z && (note.content.trim() || invite?.zone === n)) out.set(n, layoutText(note, z, measure, code));
    }
    // an invitation whose message is still empty: the code goes in all the same
    if (invite && !out.has(invite.zone)) {
      const z = template.zones.find((x) => x.number === invite.zone);
      if (z) out.set(invite.zone, layoutText({ content: '', shape: 'zone', language: 'en' }, z, measure, { code: CODE_REQUEST }));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, notes, fonts, invite?.zone]);
}

/** The code's squares, made once per link. */
function useCode(invite?: InviteCode) {
  return useMemo(() => (invite ? qrModules(invite.url) : null), [invite?.url]);
}

/** The RSVP code and its label, in the note's upright frame: on white, with a quiet margin round it. */
function CodeMark({ spot, modules, label, color }: { spot: CodeSpot; modules: boolean[][]; label: string; color: string }) {
  // the white border phone cameras look for: two modules, within the room codeSpot left round the code
  const quiet = Math.min(spot.quiet, (spot.size / modules.length) * 2);
  return (
    <g pointerEvents="none">
      <rect x={spot.x - quiet} y={spot.y - quiet} width={spot.size + 2 * quiet} height={spot.size + 2 * quiet} fill="#ffffff" />
      <path d={qrPath(modules, spot.x, spot.y, spot.size)} fill="#000000" />
      <text x={spot.labelX} y={spot.labelY} textAnchor="middle" fontFamily="'Noto Sans', sans-serif" fontSize={spot.labelSize}
        fontWeight={700} fill={color}>{label}</text>
    </g>
  );
}

/**
 * An invitation's code a second time, where the design names (template.rsvpSpot - the hexagonal
 * packet's front, 2026-10-25): on a white card over the picture, the code at the top, the label
 * under it. In the millimetres of the printed page; a view draws it through the spot's pageToView.
 */
function CodeStamp({ spot, modules, label }: { spot: MarkSpot; modules: boolean[][]; label: string }) {
  const { widthMm: w, heightMm: h } = spot;
  const pad = 2, size = w - 2 * pad, labelMm = 2.6;
  return (
    <g className="rsvp-stamp" pointerEvents="none" transform={`translate(${spot.at[0]} ${spot.at[1]}) rotate(${spot.angleDeg})`}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={1.5} fill="#ffffff" />
      <path d={qrPath(modules, -size / 2, -h / 2 + pad, size)} fill="#000000" />
      <text x={0} y={(-h / 2 + pad + size + h / 2) / 2} textAnchor="middle" dominantBaseline="central"
        fontFamily="'Noto Sans', sans-serif" fontSize={labelMm} fontWeight={700} fill="#1b2a38">{label}</text>
    </g>
  );
}

/**
 * Drag to move, wheel to zoom - on the selected zone's picture only, so the
 * first click on a zone always just selects it.
 */
function useAdjust(svg: React.RefObject<SVGSVGElement | null>, { template, pictures, selected, onAdjust, onSlide, placement = 'free' }: CardProps) {
  const zones = useMemo(() => new Map(template.zones.map((z) => [z.number, z])), [template]);
  const live = useRef({ pictures, selected, onAdjust, onSlide, placement });
  live.current = { pictures, selected, onAdjust, onSlide, placement };
  const drag = useRef<null | { zone: TemplateZone; x: number; y: number; start: ImageTransform; last: ImageTransform; toPage: Affine }>(null);
  /** Moving a shape (onSlide): where the pointer went down. */
  const slide = useRef<null | { zone: number; y: number; down: number }>(null);

  const slidable = (n: number) => !!live.current.onSlide && n === live.current.selected;
  const adjustable = (n: number) => !slidable(n) && !!live.current.onAdjust && n === live.current.selected && live.current.pictures.has(n);
  const limit = (z: TemplateZone, from: ImageTransform, to: ImageTransform) =>
    limitTransform(frameOutlines(z), z.up, live.current.pictures.get(z.number)!, from, to, live.current.placement);

  /** Screen pixels -> this SVG's own units (the view is scaled, never turned). */
  const toUser = (dx: number, dy: number): Pt => {
    const m = svg.current!.getScreenCTM()!;
    return [dx / m.a, dy / m.d];
  };

  const start = (e: ReactPointerEvent, n: number, pageToView: Affine) => {
    if (slidable(n) && e.button === 0) {
      e.preventDefault();
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
      slide.current = { zone: n, y: e.clientY, down: 0 };
      return;
    }
    if (!adjustable(n) || e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const t = live.current.pictures.get(n)!.transform || DEFAULT_TRANSFORM;
    drag.current = { zone: zones.get(n)!, x: e.clientX, y: e.clientY, start: t, last: t, toPage: invert(pageToView) };
  };
  const move = (e: ReactPointerEvent) => {
    const sl = slide.current;
    if (sl) {
      sl.down = toUser(0, e.clientY - sl.y)[1];
      live.current.onSlide?.(sl.zone, sl.down, false);
      return;
    }
    const d = drag.current;
    if (!d) return;
    const [ux, uy] = toUser(e.clientX - d.x, e.clientY - d.y);
    // view units -> page millimetres (linear part only: this is a movement, not a point)
    const [a, b, c, dd] = d.toPage;
    const page: Pt = [a * ux + c * uy, b * ux + dd * uy];
    const f = zoneFrame(frameOutlines(d.zone), d.zone.up);
    const next = {
      ...d.start,
      offsetX: d.start.offsetX + (page[0] * f.right[0] + page[1] * f.right[1]) / f.width,
      offsetY: d.start.offsetY + (page[0] * f.down[0] + page[1] * f.down[1]) / f.height,
    };
    d.last = limit(d.zone, d.last, next);
    live.current.onAdjust!(d.zone.number, d.last, false);
  };
  const end = () => {
    const sl = slide.current;
    slide.current = null;
    if (sl) live.current.onSlide?.(sl.zone, sl.down, true);
    const d = drag.current;
    drag.current = null;
    if (d) live.current.onAdjust!(d.zone.number, d.last, true);
  };

  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      const n = live.current.selected;
      if (n === null || !adjustable(n) || !(e.target as Element).closest('.zone.on')) return;
      e.preventDefault();
      const z = zones.get(n)!;
      const t = live.current.pictures.get(n)!.transform || DEFAULT_TRANSFORM;
      live.current.onAdjust!(n, limit(z, t, { ...t, scale: t.scale * Math.exp(-e.deltaY * 0.0015) }), true);
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  });

  return { start, move, end, adjustable, slidable };
}

function keySelect(n: number, onSelect?: (z: number) => void) {
  return (e: KeyboardEvent) => {
    if (onSelect && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelect(n); }
  };
}

/* --------------------------------------------------------------- numbers */

interface BadgeSpot { n: number; at: Pt; r: number; target: Pt; callout: boolean }

function insidePoly(poly: Pt[], [x, y]: Pt): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/**
 * Where each zone's number goes, so that it never covers what matters.
 *
 * In the margin beside the card, joined to the zone by a leader line: the
 * zone being edited, any zone with a note, and zones too small to take a
 * number without hiding much of themselves. Everywhere else the number sits
 * inside the zone, but low down, towards its bottom corner - pictures are
 * aimed so that the face lands on the zone's centre, which is exactly where a
 * centred number would sit. Numbers in the same margin are spread apart so
 * they never overlap.
 */
function placeBadges(zones: { n: number; poly: Pt[]; area: number; note: boolean }[], w: number, h: number, s: number,
  selected: number | null, margin: number): BadgeSpot[] {
  const out: BadgeSpot[] = [];
  const sides: Record<'left' | 'right', BadgeSpot[]> = { left: [], right: [] };
  for (const z of zones) {
    const r = Math.min(2.6 * s, Math.max(1.7 * s, 0.1 * Math.sqrt(z.area)));
    const c = centroid(z.poly);
    const callout = z.n === selected || z.note || z.area < 45 * r * r;
    if (!callout) {
      // two thirds of the way from the centre to the lowest corner: still well
      // inside a convex zone, and clear of the middle
      const low = z.poly.reduce((a, b) => (b[1] > a[1] ? b : a));
      const at: Pt = [c[0] + (low[0] - c[0]) * 0.62, c[1] + (low[1] - c[1]) * 0.62];
      out.push({ n: z.n, at, r, target: at, callout });
      continue;
    }
    const side = c[0] < w / 2 ? 'left' : 'right';
    const x = side === 'left' ? -margin / 2 : w + margin / 2;
    sides[side].push({ n: z.n, at: [x, c[1]], r, target: c, callout });
  }
  // A leader line only has to reach the zone, not cross it: end each one just
  // inside the zone's edge, so it never runs over the picture or the words.
  const polyOf = new Map(zones.map((z) => [z.n, z.poly]));
  for (const b of [...sides.left, ...sides.right]) {
    const poly = polyOf.get(b.n)!;
    const c = b.target;
    const from: Pt = [b.at[0], c[1]];
    let lo = 0, hi = 1;                       // 0 = at the margin (outside), 1 = the centre (inside)
    for (let i = 0; i < 24; i++) {
      const k = (lo + hi) / 2;
      if (insidePoly(poly, [from[0] + (c[0] - from[0]) * k, c[1]])) hi = k; else lo = k;
    }
    const edge = from[0] + (c[0] - from[0]) * hi;
    const inward = Math.min(1.8 * s, Math.abs(c[0] - edge) * 0.5) * Math.sign(c[0] - edge);
    b.target = [edge + inward, c[1]];
  }
  for (const list of Object.values(sides)) {
    list.sort((a, b) => a.at[1] - b.at[1]);
    const gap = (a: BadgeSpot, b: BadgeSpot) => a.r + b.r + 0.6 * s;
    list.forEach((b, i) => {
      b.at = [b.at[0], Math.max(b.r, i ? Math.max(b.at[1], list[i - 1].at[1] + gap(list[i - 1], b)) : b.at[1])];
    });
    for (let i = list.length - 1; i >= 0; i--) {      // then pull back up from the bottom edge
      const limit = i === list.length - 1 ? h - list[i].r : list[i + 1].at[1] - gap(list[i], list[i + 1]);
      if (list[i].at[1] > limit) list[i].at = [list[i].at[0], limit];
    }
    out.push(...list);
  }
  return out;
}

function Badges({ spots, selected }: { spots: BadgeSpot[]; selected: number | null }) {
  return (
    <g className="badges" pointerEvents="none">
      {spots.map((b) => {
        const on = b.n === selected;
        const d: Pt = [b.target[0] - b.at[0], b.target[1] - b.at[1]];
        const len = Math.hypot(d[0], d[1]) || 1;
        return (
          <g key={b.n} className={`badge${on ? ' on' : ''}`}>
            {b.callout && (
              <>
                <line x1={b.at[0] + (d[0] / len) * b.r} y1={b.at[1] + (d[1] / len) * b.r} x2={b.target[0]} y2={b.target[1]}
                  className="leader" vectorEffect="non-scaling-stroke" />
                <circle cx={b.target[0]} cy={b.target[1]} r={b.r * 0.28} className="leader-dot" />
              </>
            )}
            <circle cx={b.at[0]} cy={b.at[1]} r={b.r} />
            <text x={b.at[0]} y={b.at[1]} fontSize={b.r * 1.15} textAnchor="middle" dominantBaseline="central">{b.n}</text>
          </g>
        );
      })}
    </g>
  );
}

/**
 * The greys of the whole card in black and white (ProjectDocument.mono) - the
 * eye's own weighting of red, green and blue, as a picture's black-and-white
 * look (local/pictures.ts). Put on the pictures, the paper and the writing,
 * never on the editor's numbers and outlines.
 */
export function MonoFilter({ id }: { id: string }) {
  const row = '0.2126 0.7152 0.0722 0 0';
  return (
    <filter id={id} colorInterpolationFilters="sRGB">
      <feColorMatrix type="matrix" values={`${row} ${row} ${row} 0 0 0 1 0`} />
    </filter>
  );
}

/* ------------------------------------------------------------------ notes */

/** A note, in its zone's upright frame: the veil over any picture, then the lines. */
export function NoteText({ layout, note, veil, code }: {
  layout: TextLayout; note: ZoneText; veil: boolean; code?: { modules: boolean[][]; label: string } | null;
}) {
  const backdrop = note.backdrop ?? DEFAULT_TEXT.backdrop;
  return (
    <g pointerEvents="none">
      {veil && backdrop > 0 && <polygon points={pts(layout.shape)} fill="#ffffff" fillOpacity={backdrop} />}
      {code && layout.code && <CodeMark spot={layout.code} modules={code.modules} label={code.label} color={layout.color} />}
      {layout.lines.map((l, i) => (
        <text key={i} x={l.x} y={l.y} textAnchor={l.anchor} direction={layout.rtl ? 'rtl' : 'ltr'}
          style={{ unicodeBidi: 'embed' }} fontFamily={layout.style.font} fontSize={layout.sizeMm}
          fontWeight={layout.style.bold ? 700 : 400} fontStyle={layout.style.italic ? 'italic' : 'normal'}
          fill={layout.color}>{l.text}</text>
      ))}
    </g>
  );
}

/* ------------------------------------------------------------------ views */

function frameBox(view: { w: number; h: number }, s: number, edit: boolean, numbers: boolean, focusBox: number[] | null) {
  if (focusBox) return focusBox;
  const pad = s * 3;
  const margin = edit && numbers ? s * 8 : 0;
  return [-pad - margin, -pad, view.w + 2 * (pad + margin), view.h + 2 * pad];
}

/** One view of the opening sequence: f1 closed front ... f5 opened out. */
export function JourneyView(props: CardProps & { view: TemplateView }) {
  const { template, view, pictures, notes, symbols, selected, onSelect, onZoom, mode = 'edit', showNumbers = true, focus = false, paper, mono, plain, invite } = props;
  const edit = mode === 'edit';
  const svg = useRef<SVGSVGElement>(null);
  const placements = usePlacements(template, pictures);
  const layouts = useLayouts(template, notes, invite);
  const modules = useCode(invite);
  const codeFor = (n: number) => (invite && modules && invite.zone === n ? { modules, label: invite.label } : null);
  const noteOf = (n: number) => notes?.get(n) ?? (invite?.zone === n ? EMPTY_NOTE : undefined);
  // the paper shows in a shape left plain, and behind a note or a symbol with no picture under it
  const paperIn = (n: number) => !!plain?.has(n) || (!pictures.has(n) && (layouts.has(n) || !!symbols?.has(n)));
  const adjust = useAdjust(svg, props);
  const zones = useMemo(() => new Map(template.zones.map((z) => [z.number, z])), [template]);
  const s = Math.max(view.widthMm, view.heightMm) / 100;
  const byZone = new Map<number, typeof view.pieces>();
  for (const pc of view.pieces) byZone.set(pc.zone, [...(byZone.get(pc.zone) || []), pc]);
  const order = [...byZone.keys()].sort((a, b) => zones.get(a)!.drawOrder - zones.get(b)!.drawOrder);
  const id = `v-${view.id}${edit ? '' : '-c'}`;
  const grey = mono ? `url(#${id}-mono)` : undefined;

  const focusBox = useMemo(() => {
    if (!focus || selected === null || !byZone.has(selected)) return null;
    const p = byZone.get(selected)!.flatMap((pc) => pc.outline);
    const xs = p.map((q) => q[0]), ys = p.map((q) => q[1]);
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const pad = Math.max(x1 - x0, y1 - y0) * 0.12;
    return [x0 - pad, y0 - pad, x1 - x0 + 2 * pad, y1 - y0 + 2 * pad];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, selected, view]);

  const spots = useMemo(() => {
    if (!edit || !showNumbers || focusBox) return [];
    return placeBadges(order.map((n) => {
      const pcs = byZone.get(n)!;
      const big = pcs.reduce((a, b) => (area(b.outline) > area(a.outline) ? b : a));
      return { n, poly: big.outline, area: pcs.reduce((t, pc) => t + area(pc.outline), 0), note: layouts.has(n) };
    }), view.widthMm, view.heightMm, s, selected, s * 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, showNumbers, focusBox, view, selected, layouts]);

  const box = frameBox({ w: view.widthMm, h: view.heightMm }, s, edit, showNumbers, focusBox);

  return (
    <svg ref={svg} className="card-svg" viewBox={box.join(' ')} role="group" aria-label={view.title}
      onPointerMove={adjust.move} onPointerUp={adjust.end} onPointerCancel={adjust.end}>
      <defs>
        {view.pieces.map((pc, i) => (
          <clipPath id={`${id}-${i}`} key={i}><polygon points={pts(pc.outline)} /></clipPath>
        ))}
        <PaperDefs paper={paper} id={`${id}-paper`} />
        {mono && <MonoFilter id={`${id}-mono`} />}
        {hasPaper(paper) && (
          // the paper's colour goes where the paper shows and no zone is: around and between the shapes
          <mask id={`${id}-paper-mask`} maskUnits="userSpaceOnUse" x={-view.widthMm} y={-view.heightMm}
            width={view.widthMm * 3} height={view.heightMm * 3}>
            {view.paper.map((p, i) => <polygon key={i} points={pts(p)} fill="#ffffff" />)}
            {view.pieces.filter((pc) => !paperIn(pc.zone)).map((pc, i) => <polygon key={`z${i}`} points={pts(pc.outline)} fill="#000000" />)}
          </mask>
        )}
      </defs>
      {view.paper.map((p, i) => (
        <polygon key={i} points={pts(p)} className="paper" vectorEffect="non-scaling-stroke" />
      ))}
      {hasPaper(paper) && (
        <g mask={`url(#${id}-paper-mask)`} filter={grey}>
          <PaperFill paper={paper} id={`${id}-paper`} W={view.widthMm} H={view.heightMm} />
        </g>
      )}
      {order.map((n) => {
        const img = pictures.get(n);
        const place = placements.get(n);
        const layout = layouts.get(n);
        const z = zones.get(n)!;
        return (
          <g key={n} className={`zone${selected === n ? ' on' : ''}${adjust.adjustable(n) ? ' movable' : ''}${adjust.slidable(n) ? ' slidable' : ''}${plain?.has(n) ? ' is-plain' : ''}${paperIn(n) ? ' paper-shows' : ''}`}
            role={edit ? 'button' : undefined} tabIndex={edit ? 0 : undefined}
            aria-label={edit ? `Frame ${n}${img || layout ? '' : ', empty'}` : undefined} aria-pressed={edit ? selected === n : undefined}
            onClick={() => onSelect?.(n)} onDoubleClick={() => onZoom?.(n)} onKeyDown={keySelect(n, onSelect)}>
            {byZone.get(n)!.map((pc) => {
              const i = view.pieces.indexOf(pc);
              return (
                <g key={i} onPointerDown={(e) => adjust.start(e, n, pc.pageToView)}>
                  {img && place ? (
                    <g clipPath={`url(#${id}-${i})`}>
                      <image href={img.href} width={img.width} height={img.height} preserveAspectRatio="none"
                        transform={toSvg(compose(pc.pageToView, compose(partShift(z, pc.part ?? 0), place)))} filter={grey} />
                    </g>
                  ) : edit ? (
                    <polygon points={pts(pc.outline)} className={paperIn(n) ? 'plain' : `empty kind-${z.kind}`} />
                  ) : null}
                  {layout && noteOf(n) && (
                    <g clipPath={`url(#${id}-${i})`}>
                      <g transform={toSvg(compose(pc.pageToView, layout.toPage))} filter={grey}>
                        <NoteText layout={layout} note={noteOf(n)!} veil={!!img} code={codeFor(n)} />
                      </g>
                    </g>
                  )}
                  {symbols?.get(n) && (
                    <g clipPath={`url(#${id}-${i})`}>
                      <g transform={toSvg(pc.pageToView)} filter={grey}><SymbolMark zone={z} symbol={symbols.get(n)!} /></g>
                    </g>
                  )}
                  {edit && <polygon points={pts(pc.outline)} className="outline" vectorEffect="non-scaling-stroke" />}
                </g>
              );
            })}
          </g>
        );
      })}
      {isBrandView(template, view.id) && <BrandMark w={view.widthMm} h={view.heightMm} />}
      {template.brandSpot?.view === view.id && (
        <g transform={toSvg(template.brandSpot.pageToView)}><BrandAt spot={template.brandSpot} /></g>
      )}
      {template.rsvpSpot?.view === view.id && modules && invite && (
        <g transform={toSvg(template.rsvpSpot.pageToView)}><CodeStamp spot={template.rsvpSpot} modules={modules} label={invite.label} /></g>
      )}
      <Badges spots={spots} selected={selected} />
    </svg>
  );
}

/**
 * What a design prints on the sheet itself, fold guides or not (the fortune
 * teller, 2026-10-17): lines to cut along, each with a pair of scissors and
 * "cut here", and words on a white panel so they read on any paper.
 */
function SheetMarks({ marks }: { marks: TemplateDocument['faces'][number] }) {
  if (!marks.cuts?.length && !marks.labels?.length) return null;
  const ink = '#1b2a38';
  const font = "Inter, 'Segoe UI', Arial, sans-serif";
  return (
    <g className="sheet-marks" pointerEvents="none">
      {marks.panel && (
        <rect x={marks.panel[0]} y={marks.panel[1]} width={marks.panel[2]} height={marks.panel[3]} rx={2} fill="#ffffff" fillOpacity={0.92} />
      )}
      {marks.labels?.map((l, i) => (
        <text key={i} x={l.at[0]} y={l.at[1]} fontSize={l.size} fontWeight={l.bold ? 700 : 400} fill={ink} fontFamily={font}
          {...(l.center ? { textAnchor: 'middle', dominantBaseline: 'central' } : {})}
          {...(l.rotate ? { transform: `rotate(${l.rotate} ${l.at[0]} ${l.at[1]})` } : {})}>{l.text}</text>
      ))}
      {marks.cuts?.map(([a, b], i) => {
        const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
        const u: Pt = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
        const at: Pt = [a[0] + u[0] * 12, a[1] + u[1] * 12];
        return (
          <g key={i}>
            <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={ink} strokeWidth={0.45} />
            <g transform={`translate(${at[0]} ${at[1]}) rotate(${(Math.atan2(u[1], u[0]) * 180) / Math.PI})`}>
              <rect x={-4.2} y={-2.6} width={24} height={5.2} fill="#ffffff" />
              <circle cx={-2.6} cy={-1.5} r={1.1} fill="none" stroke={ink} strokeWidth={0.35} />
              <circle cx={-2.6} cy={1.5} r={1.1} fill="none" stroke={ink} strokeWidth={0.35} />
              <line x1={-1.7} y1={-0.9} x2={3.6} y2={0.8} stroke={ink} strokeWidth={0.35} />
              <line x1={-1.7} y1={0.9} x2={3.6} y2={-0.8} stroke={ink} strokeWidth={0.35} />
              <text x={5.5} y={0} dominantBaseline="central" fontSize={2.6} fontWeight={700} fill={ink} fontFamily={font}>cut here</text>
            </g>
          </g>
        );
      })}
    </g>
  );
}

/** One printed side of the sheet, as it comes out of the printer (side 2 already mirrored). */
export function SheetFace(props: CardProps & { face: 1 | 2; children?: ReactNode; guides?: PrintGuides['faces'][number] | null }) {
  const { template, face, pictures, notes, symbols, selected, onSelect, onZoom, mode = 'edit', showNumbers = true, focus = false, paper, mono, plain, invite } = props;
  const edit = mode === 'edit';
  const svg = useRef<SVGSVGElement>(null);
  const placements = usePlacements(template, pictures);
  const layouts = useLayouts(template, notes, invite);
  const modules = useCode(invite);
  const codeFor = (n: number) => (invite && modules && invite.zone === n ? { modules, label: invite.label } : null);
  const noteOf = (n: number) => notes?.get(n) ?? (invite?.zone === n ? EMPTY_NOTE : undefined);
  // the paper shows in a shape left plain, and behind a note or a symbol with no picture under it
  const paperIn = (n: number) => !!plain?.has(n) || (!pictures.has(n) && (layouts.has(n) || !!symbols?.has(n)));
  const adjust = useAdjust(svg, props);
  const W = template.paper.widthMm, H = template.paper.heightMm;
  const s = Math.max(W, H) / 100;
  const creases = template.faces.find((f) => f.face === face)!.creases;
  const zones = template.zones.filter((z) => z.printFace === face).sort((a, b) => a.drawOrder - b.drawOrder);
  const id = `s-${face}${edit ? '' : '-c'}`;
  const grey = mono ? `url(#${id}-mono)` : undefined;
  const brand = useMemo(() => brandOnSheet(template), [template]);
  const backView = template.views.find((v) => isBrandView(template, v.id));

  const focusZone = focus && selected !== null ? zones.find((z) => z.number === selected) : undefined;
  const focusBox = useMemo(() => {
    if (!focusZone) return null;
    const p = [focusZone.outline, ...focusZone.extraOutlines].flat();
    const xs = p.map((q) => q[0]), ys = p.map((q) => q[1]);
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const pad = Math.max(x1 - x0, y1 - y0) * 0.12;
    return [x0 - pad, y0 - pad, x1 - x0 + 2 * pad, y1 - y0 + 2 * pad];
  }, [focusZone]);

  const spots = useMemo(() => (edit && showNumbers && !focusBox
    ? placeBadges(zones.map((z) => ({ n: z.number, poly: z.outline, area: z.areaMm2, note: layouts.has(z.number) })), W, H, s, selected, s * 8)
    : []), [edit, showNumbers, focusBox, zones, W, H, s, selected, layouts]);

  // fold guides: the lines go under the pictures, so only a note with no
  // picture under it has to be kept clear; the numbers avoid all artwork
  const outlinesOf = (list: TemplateZone[]) => list.flatMap((z) => [z.outline, ...z.extraOutlines]);
  const bareNotes = useMemo(() => outlinesOf(zones.filter((z) => (layouts.has(z.number) || symbols?.has(z.number)) && !pictures.has(z.number))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template, face, layouts, pictures, symbols]);
  const artwork = useMemo(() => outlinesOf(zones.filter((z) => layouts.has(z.number) || pictures.has(z.number) || symbols?.has(z.number))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template, face, layouts, pictures, symbols]);

  // print: exactly the page, nothing around it
  const box = mode === 'clean' ? [0, 0, W, H] : frameBox({ w: W, h: H }, s, edit, showNumbers, focusBox);

  return (
    <svg ref={svg} className="card-svg" viewBox={box.join(' ')} role="group" aria-label={`Printed side ${face}`}
      onPointerMove={adjust.move} onPointerUp={adjust.end} onPointerCancel={adjust.end}>
      <defs>
        {zones.map((z) => (
          <clipPath id={`${id}-${z.number}`} key={z.number}>
            {[z.outline, ...z.extraOutlines].map((o, i) => <polygon key={i} points={pts(o)} />)}
          </clipPath>
        ))}
        {zones.filter((z) => z.shifts).flatMap((z) => [z.outline, ...z.extraOutlines].map((o, k) => (
          <clipPath id={`${id}-${z.number}-p${k}`} key={`${z.number}-p${k}`}><polygon points={pts(o)} /></clipPath>
        )))}
        <PaperDefs paper={paper} id={`${id}-paper`} />
        {mono && <MonoFilter id={`${id}-mono`} />}
        {hasPaper(paper) && (
          // the paper's colour goes around and between the zones (the shapes), not inside them
          <mask id={`${id}-paper-mask`} maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
            <rect x={0} y={0} width={W} height={H} fill="#ffffff" />
            {zones.filter((z) => !paperIn(z.number)).flatMap((z) => [z.outline, ...z.extraOutlines])
              .map((o, i) => <polygon key={i} points={pts(o)} fill="#000000" />)}
          </mask>
        )}
      </defs>
      <rect x={0} y={0} width={W} height={H} className={edit ? 'paper' : 'paper-print'} vectorEffect="non-scaling-stroke" />
      {hasPaper(paper) && (
        <g mask={`url(#${id}-paper-mask)`} filter={grey}><PaperFill paper={paper} id={`${id}-paper`} W={W} H={H} /></g>
      )}
      {edit && creases.map((c, i) => (
        <line key={i} x1={c.from[0]} y1={c.from[1]} x2={c.to[0]} y2={c.to[1]} className="crease" vectorEffect="non-scaling-stroke" />
      ))}
      {props.guides && <FoldLines guides={props.guides} notes={bareNotes} />}
      {zones.map((z) => {
        const img = pictures.get(z.number);
        const place = placements.get(z.number);
        const layout = layouts.get(z.number);
        return (
          <g key={z.number} className={`zone${selected === z.number ? ' on' : ''}${adjust.adjustable(z.number) ? ' movable' : ''}${plain?.has(z.number) ? ' is-plain' : ''}${paperIn(z.number) ? ' paper-shows' : ''}`}
            role={edit ? 'button' : undefined} tabIndex={edit ? 0 : undefined}
            aria-label={edit ? `Frame ${z.number}${img || layout ? '' : ', empty'}` : undefined}
            aria-pressed={edit ? selected === z.number : undefined}
            onClick={() => onSelect?.(z.number)} onDoubleClick={() => onZoom?.(z.number)} onKeyDown={keySelect(z.number, onSelect)}
            onPointerDown={(e) => adjust.start(e, z.number, IDENTITY)}>
            {img && place && z.shifts ? (
              // a picture printed in pieces: each piece prints its own part of the whole
              [z.outline, ...z.extraOutlines].map((_, k) => (
                <g key={k} clipPath={`url(#${id}-${z.number}-p${k})`}>
                  <image href={img.href} width={img.width} height={img.height} preserveAspectRatio="none"
                    transform={toSvg(compose(partShift(z, k), place))} filter={grey} />
                </g>
              ))
            ) : img && place ? (
              <g clipPath={`url(#${id}-${z.number})`}>
                <image href={img.href} width={img.width} height={img.height} preserveAspectRatio="none"
                  transform={toSvg(place)} filter={grey} />
              </g>
            ) : edit ? (
              [z.outline, ...z.extraOutlines].map((o, i) => (
                <polygon key={i} points={pts(o)} className={paperIn(z.number) ? 'plain' : `empty kind-${z.kind}`} />
              ))
            ) : null}
            {layout && noteOf(z.number) && (
              <g clipPath={`url(#${id}-${z.number})`}>
                <g transform={toSvg(layout.toPage)} filter={grey}>
                  <NoteText layout={layout} note={noteOf(z.number)!} veil={!!img} code={codeFor(z.number)} />
                </g>
              </g>
            )}
            {symbols?.get(z.number) && (
              <g clipPath={`url(#${id}-${z.number})`} filter={grey}><SymbolMark zone={z} symbol={symbols.get(z.number)!} /></g>
            )}
            {edit && [z.outline, ...z.extraOutlines].map((o, i) => (
              <polygon key={`o${i}`} points={pts(o)} className="outline" vectorEffect="non-scaling-stroke" />
            ))}
          </g>
        );
      })}
      {props.guides && <FoldMarkers guides={props.guides} W={W} H={H} artwork={artwork} />}
      <SheetMarks marks={template.faces.find((f) => f.face === face)!} />
      {brand && brand.face === face && (
        <g transform={toSvg(brand.viewToPage)}><BrandMark w={backView!.widthMm} h={backView!.heightMm} /></g>
      )}
      {template.brandSpot?.face === face && <BrandAt spot={template.brandSpot} />}
      {template.rsvpSpot?.face === face && modules && invite && (
        <CodeStamp spot={template.rsvpSpot} modules={modules} label={invite.label} />
      )}
      <Badges spots={spots} selected={selected} />
      {props.children}
    </svg>
  );
}
